/*
 * Kaching Bundles <-> T4S/Kalles variant compatibility bridge
 *
 * Why this exists:
 * - Kaching Bundles communicates with a Shopify-style option picker.
 * - T4S uses custom DIV swatches and a hidden SELECT[name="id"].
 * - T4S changes the product image only when that master SELECT fires change.
 *
 * This file keeps the native compatibility picker and T4S master selector in
 * sync, then lets T4S run its own existing image/price/inventory logic.
 */
(function () {
  'use strict';

  if (window.__KachingT4SVariantBridgeLoaded) return;
  window.__KachingT4SVariantBridgeLoaded = true;

  var FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  var PICKER_SELECTOR = '[data-kaching-native-variant-picker]';
  var OPTION_SELECTOR = '[data-kaching-native-option]';
  var MASTER_SELECTOR = 'select[name="id"].t4s-product__select, select[name="id"]';
  var KACHING_WIDGET_SELECTOR = 'kaching-bundles-block';
  var initializedForms = new WeakSet();
  var states = new WeakMap();

  function updateKachingWidgetState() {
    var hasKachingWidget = Boolean(document.querySelector(KACHING_WIDGET_SELECTOR));
    document.documentElement.classList.toggle('kaching-t4s-widget-ready', hasKachingWidget);
  }

  function parseJSON(element) {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent || element.innerText || '[]');
    } catch (error) {
      console.warn('[Kaching/T4S bridge] Unable to parse variant JSON.', error);
      return null;
    }
  }

  function getOptionValues(state) {
    return state.optionSelects.map(function (select) {
      return select.value;
    });
  }

  function findVariantByOptions(state, optionValues) {
    return state.variants.find(function (variant) {
      if (!Array.isArray(variant.options) || variant.options.length !== optionValues.length) {
        return false;
      }

      return variant.options.every(function (value, index) {
        return String(value) === String(optionValues[index]);
      });
    }) || null;
  }

  function findVariantById(state, variantId) {
    var id = String(variantId || '');
    return state.variants.find(function (variant) {
      return String(variant.id) === id;
    }) || null;
  }

  function updateStateSnapshots(state) {
    state.lastMasterValue = String(state.master.value || '');
    state.lastOptionValues = getOptionValues(state).join('\u001f');
  }

  function syncOptionsFromMaster(state) {
    var variant = findVariantById(state, state.master.value);
    if (!variant || !Array.isArray(variant.options)) {
      updateStateSnapshots(state);
      return;
    }

    state.syncing = true;
    state.optionSelects.forEach(function (select, index) {
      if (variant.options[index] !== undefined) {
        select.value = String(variant.options[index]);
      }
    });
    state.syncing = false;
    updateStateSnapshots(state);
  }

  function dispatchMasterChange(master) {
    master.dispatchEvent(new Event('change', {
      bubbles: true,
      cancelable: true
    }));
  }

  function setMasterVariant(state, variant, source) {
    if (!variant) return false;

    var nextId = String(variant.id);
    var changed = String(state.master.value) !== nextId;

    state.syncing = true;
    state.master.value = nextId;
    state.syncing = false;

    if (String(state.master.value) !== nextId) {
      console.warn('[Kaching/T4S bridge] Variant ID is not present in the T4S master selector:', nextId);
      updateStateSnapshots(state);
      return false;
    }

    /*
     * Always dispatch after a native picker action. Some apps set the same
     * variant ID again but still expect the theme to refresh its media state.
     */
    if (changed || source === 'native-picker' || source === 'poll') {
      dispatchMasterChange(state.master);
    }

    updateStateSnapshots(state);
    return true;
  }

  function syncMasterFromOptions(state, source) {
    if (state.syncing) return;
    var variant = findVariantByOptions(state, getOptionValues(state));
    if (variant) setMasterVariant(state, variant, source || 'native-picker');
  }

  function getSiblingStates(state) {
    if (!state.productId) return [state];

    var siblings = [];
    document.querySelectorAll(FORM_SELECTOR).forEach(function (form) {
      if (String(form.getAttribute('data-productid') || '') !== state.productId) return;
      var siblingState = states.get(form);
      if (siblingState) siblings.push(siblingState);
    });

    return siblings.length ? siblings : [state];
  }

  function syncProductFormsFromOptions(state, source) {
    var optionValues = getOptionValues(state);
    var variant = findVariantByOptions(state, optionValues);
    if (!variant) return;

    getSiblingStates(state).forEach(function (siblingState) {
      siblingState.syncing = true;
      siblingState.optionSelects.forEach(function (select, index) {
        if (optionValues[index] !== undefined) select.value = optionValues[index];
      });
      siblingState.syncing = false;
      setMasterVariant(siblingState, variant, source || 'native-picker');
    });
  }

  function onNativeOptionEvent(event) {
    var option = event.target.closest ? event.target.closest(OPTION_SELECTOR) : null;
    if (!option) return;

    var form = option.form || option.closest(FORM_SELECTOR);
    var state = form ? states.get(form) : null;
    if (!state || state.syncing) return;

    syncProductFormsFromOptions(state, 'native-picker');
  }

  function initializeForm(form) {
    if (!form || initializedForms.has(form)) return;

    var picker = form.querySelector(PICKER_SELECTOR);
    var master = form.querySelector(MASTER_SELECTOR);
    if (!picker || !master) return;

    var optionSelects = Array.prototype.slice.call(picker.querySelectorAll(OPTION_SELECTOR));
    var variants = parseJSON(picker.querySelector('[data-kaching-native-variants]'));
    if (!optionSelects.length || !Array.isArray(variants) || !variants.length) return;

    var state = {
      form: form,
      picker: picker,
      master: master,
      optionSelects: optionSelects,
      variants: variants,
      productId: String(form.getAttribute('data-productid') || picker.getAttribute('data-product-id') || ''),
      syncing: false,
      lastMasterValue: '',
      lastOptionValues: ''
    };

    states.set(form, state);
    initializedForms.add(form);

    master.addEventListener('change', function () {
      if (!state.syncing) syncOptionsFromMaster(state);
      else updateStateSnapshots(state);
    });

    syncOptionsFromMaster(state);
  }

  function initializeWithin(root) {
    if (!root || !root.querySelectorAll) return;

    if (root.matches && root.matches(FORM_SELECTOR)) initializeForm(root);
    root.querySelectorAll(FORM_SELECTOR).forEach(initializeForm);
  }

  function pollForSilentAppChanges() {
    document.querySelectorAll(FORM_SELECTOR).forEach(function (form) {
      var state = states.get(form);
      if (!state || state.syncing) return;

      var currentMasterValue = String(state.master.value || '');
      var currentOptionValues = getOptionValues(state).join('\u001f');

      if (currentOptionValues !== state.lastOptionValues) {
        syncProductFormsFromOptions(state, 'poll');
        return;
      }

      if (currentMasterValue !== state.lastMasterValue) {
        /*
         * Handles apps that assign select.value without firing a change event.
         * Dispatching here invokes T4S's existing image-change routine.
         */
        dispatchMasterChange(state.master);
        syncOptionsFromMaster(state);
      }
    });
  }

  document.addEventListener('change', onNativeOptionEvent, true);
  document.addEventListener('input', onNativeOptionEvent, true);

  function start() {
    initializeWithin(document);
    updateKachingWidgetState();

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) initializeWithin(node);
        });
      });
      updateKachingWidgetState();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    window.setInterval(pollForSilentAppChanges, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
